import React, { useState, useEffect, useCallback } from 'react';
import AdminHeader from './AdminHeader';
import PageHeader from './PageHeader';
import { FaCheckCircle, FaTrash, FaEye, FaClock, FaComment } from 'react-icons/fa';
import './CommentManager.scss';

interface Comment {
  id: number;
  blogId: number;
  author: string;
  email?: string;
  text: string;
  approved: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Blog {
  id: number;
  title: string;
  slug: string;
}

const CommentManager: React.FC = () => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved'>('all');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  const API_BASE = 'http://localhost:4000/api';
  const token = localStorage.getItem('adminToken');

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/comments/admin/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setComments(data);
      } else {
        showMessage('Failed to fetch comments', 'error');
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      showMessage('Error fetching comments', 'error');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchBlogs = async () => {
    try {
      const response = await fetch(`${API_BASE}/blogs`);
      if (response.ok) {
        const data = await response.json();
        setBlogs(data);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    }
  };

  useEffect(() => {
    fetchComments();
    fetchBlogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showMessage = (msg: string, type: 'success' | 'error') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const approveComment = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE}/comments/admin/${id}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        showMessage('Comment approved successfully!', 'success');
        fetchComments();
      } else {
        showMessage('Failed to approve comment', 'error');
      }
    } catch (error) {
      console.error('Error approving comment:', error);
      showMessage('Error approving comment', 'error');
    }
  };

  const deleteComment = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/comments/admin/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        showMessage('Comment deleted successfully!', 'success');
        fetchComments();
      } else {
        showMessage('Failed to delete comment', 'error');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      showMessage('Error deleting comment', 'error');
    }
  };

  const getBlogTitle = (blogId: number): string => {
    const blog = blogs.find(b => b.id === blogId);
    return blog ? blog.title : `Blog #${blogId}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredComments = comments.filter(comment => {
    if (filter === 'pending') return !comment.approved;
    if (filter === 'approved') return comment.approved;
    return true;
  });

  const pendingCount = comments.filter(c => !c.approved).length;
  const approvedCount = comments.filter(c => c.approved).length;

  return (
    <div className="comment-manager">
      <AdminHeader />
      <div className="comment-manager__container">
        <PageHeader 
          title="Comment Management" 
          subtitle="Approve, reject, or delete user comments"
        />

        {message && (
          <div className={`comment-manager__message comment-manager__message--${messageType}`}>
            {message}
          </div>
        )}

        {/* Stats */}
        <div className="comment-manager__stats">
          <div className="stat-card">
            <FaComment className="stat-icon" />
            <div className="stat-info">
              <h3>{comments.length}</h3>
              <p>Total Comments</p>
            </div>
          </div>
          <div className="stat-card stat-card--warning">
            <FaClock className="stat-icon" />
            <div className="stat-info">
              <h3>{pendingCount}</h3>
              <p>Pending Approval</p>
            </div>
          </div>
          <div className="stat-card stat-card--success">
            <FaCheckCircle className="stat-icon" />
            <div className="stat-info">
              <h3>{approvedCount}</h3>
              <p>Approved</p>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="comment-manager__filters">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Comments ({comments.length})
          </button>
          <button
            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            Pending ({pendingCount})
          </button>
          <button
            className={`filter-btn ${filter === 'approved' ? 'active' : ''}`}
            onClick={() => setFilter('approved')}
          >
            Approved ({approvedCount})
          </button>
        </div>

        {/* Comments List */}
        {loading ? (
          <div className="comment-manager__loading">
            <div className="spinner"></div>
            <p>Loading comments...</p>
          </div>
        ) : filteredComments.length === 0 ? (
          <div className="comment-manager__empty">
            <FaComment />
            <h3>No comments found</h3>
            <p>
              {filter === 'pending' && 'There are no pending comments to review.'}
              {filter === 'approved' && 'No approved comments yet.'}
              {filter === 'all' && 'No comments have been submitted yet.'}
            </p>
          </div>
        ) : (
          <div className="comment-manager__list">
            {filteredComments.map(comment => (
              <div
                key={comment.id}
                className={`comment-card ${comment.approved ? 'comment-card--approved' : 'comment-card--pending'}`}
              >
                <div className="comment-card__header">
                  <div className="comment-card__user">
                    <div className="comment-card__avatar">
                      {comment.author.charAt(0).toUpperCase()}
                    </div>
                    <div className="comment-card__user-info">
                      <h4>{comment.author}</h4>
                      {comment.email && <p className="email">{comment.email}</p>}
                    </div>
                  </div>
                  <div className="comment-card__status">
                    {comment.approved ? (
                      <span className="badge badge--success">
                        <FaCheckCircle /> Approved
                      </span>
                    ) : (
                      <span className="badge badge--warning">
                        <FaClock /> Pending
                      </span>
                    )}
                  </div>
                </div>

                <div className="comment-card__content">
                  <p>{comment.text}</p>
                </div>

                <div className="comment-card__meta">
                  <div className="comment-card__blog">
                    <FaEye />
                    <span>On: {getBlogTitle(comment.blogId)}</span>
                  </div>
                  <div className="comment-card__date">
                    {formatDate(comment.createdAt)}
                  </div>
                </div>

                <div className="comment-card__actions">
                  {!comment.approved && (
                    <button
                      className="btn btn--approve"
                      onClick={() => approveComment(comment.id)}
                    >
                      <FaCheckCircle /> Approve
                    </button>
                  )}
                  <button
                    className="btn btn--delete"
                    onClick={() => deleteComment(comment.id)}
                  >
                    <FaTrash /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentManager;

